<div
  class="container"
  cdkDropListGroup
  [ngClass]="{ 'printer-container': isPrinter || isOutlook }"
>
  <aside class="sidebar">
    <div
      class="head-label"
      id="docListLabel"
    >
      {{ getLabelKey('headerLabelKey') | translate }}

      <div class="select-all-documents">
        <p-checkbox
          class="dap-select-all-docs"
          inputId="selectAllDocs"
          [binary]="true"
          [(ngModel)]="selectAll"
        />
        <label
          class="checkbox-label"
          for="selectAllDocs"
          >{{ 'selectAllDocuments' | translate }}</label
        >
      </div>
    </div>

    <ul
      id="docList"
      class="document-list"
      role="group"
      aria-labelledby="docListLabel"
    >
      @for (file of attachments; track file.id) {
        @let disabledDocument = disableFileFromSelection(file);
        <li
          class="document-item"
          [ngClass]="{ 'document-item-disabled': disabledDocument }"
        >
          <p-checkbox
            class="dap-file-selection"
            [inputId]="file.id"
            [binary]="true"
            [disabled]="disabledDocument"
            [ngModel]="isSelected(file)"
            (ngModelChange)="toggleFileSelection(file, $event)"
          ></p-checkbox>
          <label
            #labelRef
            class="file-name-label"
            tooltipPosition="right"
            showDelay="500"
            hideDelay="300"
            [for]="file.id"
            [pTooltip]="file.name"
            [tooltipDisabled]="!isTextOverflowing(labelRef)"
          >
            {{ file.name }}
          </label>
        </li>
      }
    </ul>
    <div
      class="visually-hidden"
      aria-live="polite"
    >
      {{ selectedFiles.length }} / {{ attachments.length }} {{ 'selected' | translate }}
    </div>
  </aside>
  <div class="workspace">
    <div class="bulk-actions-header">
      <div class="header-text">
        <h2 class="bulk-actions-title">{{ getLabelKey('pageHeaderKey') | translate }}</h2>
        <span class="bulk-actions-description">
          {{ getLabelKey('helperMessageKey') | translate }}
        </span>
      </div>
    </div>
    @if (bulkActionType === 'update') {
      <div
        class="update-documents"
        [formGroup]="docFormGroup"
      >
        <div class="input-container">
          <label
            class="input-label"
            for="areaSelector"
            >{{ 'area' | translate }}</label
          >
          <p-select
            placeholder="{{ 'defaultAreaPlaceholder' | translate }}"
            class="dap-select-area input-field"
            formControlName="area"
            id="areaSelector"
            optionLabel="name"
            optionValue="code"
            filterBy="name"
            [showClear]="!!docFormGroup.get('area')?.value"
            [attr.aria-required]="true"
            [options]="docAreas"
            [filter]="true"
            [appendTo]="'body'"
            (onChange)="onAreaChange($event)"
          ></p-select>
        </div>
        <div class="input-container">
          <label
            class="input-label"
            for="documentTypeSelector"
            >{{ 'documentType' | translate }}</label
          >
          <p-select
            class="dap-select-doc-type input-field"
            optionLabel="name"
            optionValue="code"
            filterBy="name"
            formControlName="documentTypeCode"
            id="documentTypeSelector"
            placeholder="Select a document type"
            [showClear]="!!docFormGroup.get('documentTypeCode')?.value"
            [options]="filteredDocumentTypes"
            [filter]="true"
            [appendTo]="'body'"
            [disabled]="!docFormGroup.get('area')?.value"
            (onChange)="onDocumentTypeChange($event)"
          >
          </p-select>
        </div>
      </div>
    }
    <div
      class="selected-documents-container"
      cdkDropList
      id="selectedDocumentsList"
      [cdkDropListData]="selectedFiles"
      (cdkDropListDropped)="drop($event)"
    >
      @if (selectedFiles.length === 0) {
        <div class="no-files-message">
          {{ getLabelKey('emptyMessageKey') | translate }}
        </div>
      }

      @for (file of selectedFiles; track file; let i = $index) {
        <div
          cdkDrag
          cdkDragLockAxis="y"
        >
          <!-- Custom Drag Preview -->
          <ng-container *cdkDragPreview>
            <p-card>
              <span class="file-name-preview">{{ file.name }}</span>
            </p-card>
          </ng-container>
          <p-card>
            <div
              class="document-card"
              [ngClass]="{ 'keyboard-move-active': isMoveActive && activeItemIndex === i }"
            >
              @if (bulkActionType === 'merge') {
                <div
                  *cdkDragPlaceholder
                  class="drag-line-placeholder"
                ></div>
                <p-button
                  class="dap-drag-btn drag-button move-document-button"
                  icon="fa-regular fa-grip-dots-vertical"
                  severity="contrast"
                  variant="text"
                  cdkDragHandle
                  [ngClass]="{ 'no-focus-visible': isMoveActive }"
                  [ariaLabel]="'moveDocument' | translate"
                  [rounded]="true"
                  (keydown)="trapFocus($event, i)"
                  (keyup)="moveItem($event, i)"
                >
                </p-button>
              }
              <div class="document-info">
                <span class="file-name">{{ file.name }}</span>
                <div class="document-keyfield-data">
                  @if (bulkActionType === 'update') {
                    <div>
                      <span class="doc-data-labels">{{ 'area' | translate }}:</span>
                      {{ getAreaLabel(file) }}
                    </div>
                    <div>
                      <span class="doc-data-labels">{{ 'documentType' | translate }}: </span>
                      {{ getDocumentTypeLabel(file) }}
                    </div>
                  }
                </div>
              </div>

              <p-button
                class="dap-remove-doc-btn"
                icon="fa-solid fa-circle-x"
                severity="contrast"
                variant="text"
                size="small"
                [ariaLabel]="'removeDocument' | translate"
                [rounded]="true"
                (click)="removeFile(file)"
              >
              </p-button>
            </div>
          </p-card>
        </div>
      }
    </div>
    <div class="toolbar">
      <div class="action-footer-right">
        <p-button
          class="dap-cancel-bulk-action"
          variant="text"
          [label]="'cancel' | translate"
          (onClick)="closeBulkAction()"
        />
        <p-button
          class="dap-complete-bulk-action-btn"
          severity="secondary"
          [label]="getLabelKey('headerLabelKey') | translate"
          [disabled]="
            bulkActionType === 'update'
              ? selectedFiles.length < 1 || !docFormGroup.get('area')?.value
              : selectedFiles.length < 2
          "
          (onClick)="openBulkActionsDialog()"
        />
      </div>
    </div>
  </div>
  <p-dialog
    header="{{ getLabelKey('headerLabelKey') | translate }}"
    [modal]="true"
    [closable]="true"
    [resizable]="false"
    [(visible)]="isDialogVisible"
    (onHide)="closeBulkActionDialog()"
  >
    <p class="dialog-content">{{ getLabelKey('dialogMessageKey') | translate }}</p>
    <p class="confirm-message">{{ getLabelKey('confirmMessageKey') | translate }}</p>
    <div class="dialog-footer">
      <p-button
        class="dap-confirm-cancel"
        variant="text"
        [label]="'cancel' | translate"
        (onClick)="closeBulkActionDialog()"
      >
      </p-button>
      <p-button
        class="dap-confirm-bulk-action"
        [label]="'confirm' | translate"
        (onClick)="onConfirm()"
      >
      </p-button>
    </div>
  </p-dialog>
</div>
@if (isMergeLoading) {
  <div class="loading-spinner-overlay">
    <p-progressSpinner ariaLabel="document merging in progress" />
  </div>
}
